import { keyBy, remove } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import Helmet from 'react-helmet'
import { facebook } from './config'
import { getSortLink } from 'servers/recommend'
import { useTranslation } from '@volcengine/i18n'

import styles from './styles.module.scss'
import { isIOS, openBrowser } from '@docs/common'
import useLanguageTrans from 'common/useLanguageTrans'


type IShareType = 'email' | 'facebook' | 'twitter' | 'messenger' | 'copylink'

type IShareInfo = {
  method: IShareType
  link?: string
  title?: string
  content?: string
  image?: string
}

interface IProps {
  visible: boolean
  shares: IShareInfo[]
  title: string
  content: string
  link: string
  image?: string
  setVisible: (visible: boolean) => void
  onShareEmail?: () => void
  onShareTwitter?: () => void
  onShareMessenger?: () => void
  onShareFacebook?: () => void
  onCopyLink?: () => void
}

const encode = (content: string) => {
  return window.btoa(unescape(encodeURIComponent(content)))
}

const InnerShare = ({
  visible = false,
  title,
  shares,
  link,
  content,
  image,
  onShareEmail,
  onShareFacebook,
  onShareMessenger,
  onShareTwitter,
  onCopyLink,
  setVisible,
}: IProps) => {
  const [isCopied, setIsCopied] = useState(false)
  const [sortLink, setSortLink] = useState('')

  const iframeRef = useRef<HTMLIFrameElement>(null)
  useLanguageTrans()
  const { t } = useTranslation()
  const allIcons = useMemo(() => remove(shares, o => o.method !== 'copylink'), [shares])

  const hasCopyLink = useMemo(() => shares.some(share => share.method === 'copylink'), [shares])

  // 生成分享信息
  const allShares = useMemo(
    () =>
      shares.map(item => ({
        ...item,
        title: item.title || title,
        link: item.link || link,
        content: item.content || content,
        image: item.image || image,
      })),
    [shares, link, content, image, title]
  )

  // 生成分享信息
  const shareInfos = useMemo(() => keyBy(allShares, 'method'), [allShares])

  // 处理分享
  const handleShare = (method: IShareType) => {
    let shareUrl = ''

    const linkUrl = encodeURIComponent(link)
    const iframeEle = iframeRef.current!

    if (method === 'email') {
      const emailContent = `${title}\n${content}\n${sortLink}`
      const base64Str = encode(JSON.stringify({ title, content: emailContent }))
      iframeEle.src = `eufysecurity://eufy/explore/emailTo#${base64Str}`
      onShareEmail && onShareEmail()
      setVisible(false)
      return
    }

    if (method === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(content)}&url=${encodeURIComponent(
        sortLink
      )}?jump=2`
      onShareTwitter && onShareTwitter()
    }

    if (method === 'messenger') {
      const appLink = `fb-messenger://share/?link=${linkUrl}&app_id=${facebook.appId}`
      shareUrl = `eufysecurity://eufy/explore/shareTo#${encode(JSON.stringify({ appLink }))}`
      onShareMessenger && onShareMessenger()
    }

    if (method === 'facebook') {
      const webLink = `https://www.facebook.com/sharer/sharer.php?u=${linkUrl}`
      onShareFacebook && onShareFacebook()
      openBrowser(webLink)
      setVisible(false)
      return
    }

    if (shareUrl) {
      isIOS ? (iframeEle.src = shareUrl) : (window.location.href = shareUrl)
    }
    setVisible(false)
  }

  // 复制链接
  const handleCopy = () => {
    setIsCopied(true)
    setVisible(false)
    onCopyLink && onCopyLink()
  }

  // 获取短链
  useEffect(() => {
    getSortLink(link).then(data => {
      setSortLink(data)
    })
  }, [link])


  return (
    <>
      <Helmet
        title={title}
        meta={[
          { property: 'og:url', content: shareInfos?.facebook?.link },
          { property: 'og:type', content: 'website' },
          { property: 'og:description', content: content },
          { property: 'og:title', content: title },
          { property: 'og:image', content: image },
          { property: 'fb:app_id', content: facebook.appId },
          { property: 'twitter:card', content: 'summary_large_image' },
          { property: 'twitter:image', content: image },
          // { name: 'keywords', content: lang.keywords },
          { name: 'description', content: content },
        ]}
      />
      <div className={styles.share} style={{ display: visible ? '' : 'none' }}>
        <div className={styles.mask} onClick={() => setVisible(false)}></div>
        <div className={styles.body}>
          <h1>{t('rec_share')}</h1>
          <div className={styles.icons}>
            {allIcons.map(item => (
              <i className={styles[item.method]} key={item.method} onClick={() => handleShare(item.method)}></i>
            ))}
          </div>
          {hasCopyLink && (
            <div className={styles.copylink}>
              <span className={styles.link}>{sortLink}</span>
              <CopyToClipboard text={sortLink} onCopy={handleCopy}>
                <span className={styles.copy}>{isCopied ? t('rec_copied') : t('rec_copy')}</span>
              </CopyToClipboard>
            </div>
          )}
        </div>
      </div>
      <iframe title="email 分享" id={'referral-iframe'} ref={iframeRef} style={{ display: 'none' }} frameBorder="0"></iframe>
    </>
  )
}

export default InnerShare
